import { Badge, Box, Chip, Paper, Stack, Typography } from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { FaCrown } from "react-icons/fa";
import PlayerAvatar from "../PlayerAvatar/PlayerAvatar";
import numeral from "numeral";
import useUiScale from "../../shared/helper/useUiScale";

export interface PlayerCardProps {
  name: string;
  score?: number;
  inGame?: boolean;
  isHost: boolean;
  isTsar?: boolean;
  isSelf?: boolean;
  isReady?: boolean;
  color?: string;
  canKick?: boolean;
  onKick?: () => void;
}


const formatPoints = (n:number) => {
  if (n < 1e3) return n;
  if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + "K";
  if (n >= 1e6) return +(n / 1e6).toFixed(1) + "M";
};

export default function PlayerCard(props: PlayerCardProps) {
  // Avatars are bitmaps, so their size is a number rather than a rem length.
  const uiScale = useUiScale();
  const avatarLarge = Math.round(64 * uiScale);
  const avatarSmall = Math.round(24 * uiScale);
  let playerIcon = props.color
    ? <PlayerAvatar color={props.color} size={avatarLarge} />
    : <Box sx={{ width: avatarLarge, height: avatarLarge, borderRadius: "50%", bgcolor: "grey.300" }} />;

  if (props.isHost) {
    playerIcon = (
      <Badge
        overlap="circular"
        badgeContent={<FaCrown />}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        color="primary"
        sx={{ zIndex: 0 }}
      >
        {playerIcon}
      </Badge>
    );
  }

  if (props.isReady !== undefined) {
    playerIcon = (
      <Badge
        overlap="circular"
        badgeContent={props.isReady
          ? <CheckCircleIcon sx={{ fontSize: "0.9em" }} />
          : <RadioButtonUncheckedIcon sx={{ fontSize: "0.9em" }} />}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        color={props.isReady ? "primary" : "secondary"}
        sx={{ zIndex: 0 }}
      >
        {playerIcon}
      </Badge>
    );
  }

  
  return (
    <>
      {props.inGame && props.score !== undefined ? (
        <Paper sx={{ width: "calc(100%)", height: "4em" }} elevation={3}>
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ height: "100%", paddingInline: ".5em" }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
              {props.isReady !== undefined && (
                props.isReady
                  ? <CheckCircleIcon color="primary" sx={{ fontSize: "1em", flexShrink: 0 }} />
                  : <RadioButtonUncheckedIcon color="secondary" sx={{ fontSize: "1em", flexShrink: 0 }} />
              )}
              {props.color && <PlayerAvatar color={props.color} size={avatarSmall} />}
              {props.isHost
                ? <FaCrown style={{ color: "gold", flexShrink: 0 }} />
                : props.canKick
                  ? <CloseIcon
                      sx={{ fontSize: "1em", flexShrink: 0, color: "text.disabled", cursor: "pointer", "&:hover": { color: "error.main" } }}
                      onClick={(e) => { e.stopPropagation(); props.onKick?.(); }}
                    />
                  : <Box sx={{ width: "1em", flexShrink: 0 }} />}
              <Typography
                sx={{
                  color: props.isTsar ? "primary.main" : props.isSelf ? "primary.main" : "",
                  fontWeight: props.isSelf ? "bold" : undefined,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                variant="body1"
              >
                {props.name}
              </Typography>
              {props.isTsar && <Chip label="T" size="small" color="primary" sx={{ flexShrink: 0, height: "1.4em", fontSize: "0.6em" }} />}
            </Stack>
            <Typography variant="body1" sx={{ flexShrink: 0, ml: 0.5 }}>
              {props.score > 1000 ? numeral(props.score).format('0.0a') : props.score}
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <Paper sx={{ minWidth: "9em", height: "10em" }} elevation={3}>
          <Stack
            direction="column"
            sx={{ height: "100%", padding: "0.4em" }}
            justifyContent="center"
            spacing={1}
            alignItems="center"
          >
            <Stack justifyContent={"center"}>{playerIcon}</Stack>

            <Typography variant="h6">{props.name}</Typography>
          </Stack>
        </Paper>
      )}
    </>
  );
}
